import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Trash2, ArrowLeft, Loader, RefreshCw } from 'lucide-react';
import { useCartStore } from '@/store';
import { useCreateCheckout } from '@/hooks/useApi';
import { apiClient } from '@/lib/api-client';
import { Listing } from '@/types';
import { formatPrice } from '@/utils';
import CardImage from '@/components/CardImage';
import toast from 'react-hot-toast';

export default function CartPage() {
  const { items, removeItem, clearCart } = useCartStore();
  const navigate = useNavigate();
  const { mutate: createCheckout } = useCreateCheckout();

  const [listings, setListings] = useState<Record<string, Listing>>({});
  const [loading, setLoading] = useState(false);
  const [checkingOutId, setCheckingOutId] = useState<string | null>(null);

  const fetchListings = () => {
    if (items.length === 0) return;
    setLoading(true);
    Promise.all(
      items.map(({ listingId }) =>
        apiClient
          .get<any>(`/listings/${listingId}`)
          .then((res) => [listingId, res.data?.data ?? res.data?.listing ?? res.data] as const)
          .catch(() => [listingId, null] as const)
      )
    ).then((results) => {
      const map: Record<string, Listing> = {};
      results.forEach(([id, listing]) => { if (listing) map[id] = listing; });
      setListings(map);
      setLoading(false);
    });
  };

  useEffect(() => { fetchListings(); }, [items.length]);

  const saleItems = items.filter(({ listingId }) => {
    const l = listings[listingId];
    return l && (l.listing_type === 'sale' || l.listing_type === 'both' || l.listing_type === 'auction');
  });

  const tradeItems = items.filter(({ listingId }) => {
    const l = listings[listingId];
    return l && l.listing_type === 'trade';
  });

  const saleSubtotal = saleItems.reduce((sum, { listingId }) => {
    const l = listings[listingId];
    return sum + (l?.price ?? 0);
  }, 0);

  const shippingTotal = saleItems.reduce((sum, { listingId }) => {
    const l = listings[listingId];
    return sum + (l?.shipping_price ?? 0);
  }, 0);

  const handleCheckout = (listingId: string) => {
    setCheckingOutId(listingId);
    createCheckout(listingId, {
      onSuccess: ({ url }) => { window.location.href = url; },
      onError: () => {
        toast.error('Could not start checkout. Please try again.');
        setCheckingOutId(null);
      },
    });
  };

  return (
    <div className="bg-gray-50 dark:bg-zinc-950 min-h-screen">
      {/* Header */}
      <div className="bg-gradient-to-r from-brand-600 to-accent-500 text-white py-8">
        <div className="container-custom">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-brand-100 hover:text-white mb-3 text-sm transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
          <h1 className="text-2xl font-display font-bold">Cart</h1>
          <p className="text-brand-100 text-sm mt-0.5 font-medium tracking-wide">
            Buy · Sell · Trade · Play
          </p>
        </div>
      </div>

      <div className="container-custom py-8 max-w-3xl">

        {/* Empty state */}
        {items.length === 0 && (
          <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-800 shadow-sm p-12 text-center">
            <ShoppingCart className="h-12 w-12 mx-auto text-gray-300 dark:text-zinc-700 mb-4" />
            <h2 className="text-xl font-bold mb-2">Your cart is empty</h2>
            <p className="text-sm text-gray-500 dark:text-zinc-500 mb-6 max-w-sm mx-auto">
              Add listings here to buy or save trade offers to review — your cart is the central place for both.
            </p>
            <div className="flex gap-3 justify-center flex-wrap">
              <Link to="/marketplace" className="btn-primary">Browse Marketplace</Link>
              <Link to="/wishlist" className="btn-outline">View Wishlist</Link>
            </div>
          </div>
        )}

        {/* Loading */}
        {items.length > 0 && loading && (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
          </div>
        )}

        {/* Items */}
        {items.length > 0 && !loading && (
          <div className="space-y-6">

            {/* Buy / Auction */}
            {saleItems.length > 0 && (
              <section>
                <h2 className="text-xs font-semibold text-gray-500 dark:text-zinc-500 uppercase tracking-wider mb-3">
                  Purchases
                </h2>
                <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-800 shadow-sm divide-y divide-gray-100 dark:divide-zinc-800">
                  {saleItems.map(({ listingId }) => {
                    const l = listings[listingId];
                    if (!l) return null;
                    const isAuction = l.listing_type === 'auction';
                    const isCheckingThis = checkingOutId === listingId;
                    return (
                      <div key={listingId} className="flex items-center gap-4 p-4">
                        <Link to={`/listings/${listingId}`} className="h-16 w-12 rounded-lg overflow-hidden flex-shrink-0">
                          <CardImage
                            src={l.image_urls?.[0]}
                            alt={l.card?.name ?? l.title}
                            cardName={l.card?.name}
                            game={l.card?.game}
                            className="w-full h-full"
                            compact
                          />
                        </Link>
                        <div className="flex-1 min-w-0">
                          <Link
                            to={`/listings/${listingId}`}
                            className="font-medium text-sm hover:text-brand-600 dark:hover:text-brand-400 truncate block transition-colors"
                          >
                            {l.card?.name ?? l.title}
                          </Link>
                          <p className="text-xs text-gray-500 dark:text-zinc-500">
                            {l.condition} · {l.seller?.username}
                          </p>
                          {l.shipping_price > 0 && (
                            <p className="text-xs text-gray-400 dark:text-zinc-600">
                              +{formatPrice(l.shipping_price)} shipping
                            </p>
                          )}
                        </div>
                        <p className="font-bold text-brand-600 dark:text-brand-400 flex-shrink-0">
                          {isAuction
                            ? `From ${formatPrice(l.starting_price ?? l.price)}`
                            : formatPrice(l.price)}
                        </p>
                        {isAuction ? (
                          <Link
                            to={`/listings/${listingId}`}
                            className="px-3 py-2 bg-orange-500 text-white rounded-xl text-sm font-medium hover:bg-orange-600 flex-shrink-0 transition-colors"
                          >
                            Bid
                          </Link>
                        ) : (
                          <button
                            onClick={() => handleCheckout(listingId)}
                            disabled={!!checkingOutId}
                            className="px-3 py-2 bg-brand-600 text-white rounded-xl text-sm font-medium hover:bg-brand-700 disabled:opacity-50 flex-shrink-0 flex items-center gap-1.5 transition-colors"
                          >
                            {isCheckingThis
                              ? <><Loader className="h-3.5 w-3.5 animate-spin" /> …</>
                              : 'Buy Now'}
                          </button>
                        )}
                        <button
                          onClick={() => removeItem(listingId)}
                          className="text-gray-300 dark:text-zinc-700 hover:text-red-500 transition-colors flex-shrink-0"
                          title="Remove"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>

                {/* Subtotal */}
                <div className="mt-3 flex justify-end">
                  <div className="bg-white dark:bg-zinc-900 rounded-xl border border-gray-200 dark:border-zinc-800 px-5 py-3 min-w-52">
                    <div className="flex justify-between gap-8 text-sm mb-1">
                      <span className="text-gray-500 dark:text-zinc-500">Items</span>
                      <span>{formatPrice(saleSubtotal)}</span>
                    </div>
                    {shippingTotal > 0 && (
                      <div className="flex justify-between gap-8 text-sm mb-1">
                        <span className="text-gray-500 dark:text-zinc-500">Shipping</span>
                        <span>{formatPrice(shippingTotal)}</span>
                      </div>
                    )}
                    <div className="flex justify-between gap-8 text-sm font-bold border-t border-gray-100 dark:border-zinc-800 pt-2 mt-1">
                      <span>Total</span>
                      <span>{formatPrice(saleSubtotal + shippingTotal)}</span>
                    </div>
                    <p className="text-xs text-gray-400 dark:text-zinc-600 mt-1.5">Each item checks out separately</p>
                  </div>
                </div>
              </section>
            )}

            {/* Trade offers */}
            {tradeItems.length > 0 && (
              <section>
                <h2 className="text-xs font-semibold text-gray-500 dark:text-zinc-500 uppercase tracking-wider mb-3">
                  Trade Offers
                </h2>
                <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-800 shadow-sm divide-y divide-gray-100 dark:divide-zinc-800">
                  {tradeItems.map(({ listingId }) => {
                    const l = listings[listingId];
                    if (!l) return null;
                    return (
                      <div key={listingId} className="flex items-center gap-4 p-4">
                        <Link to={`/listings/${listingId}`} className="h-16 w-12 rounded-lg overflow-hidden flex-shrink-0">
                          <CardImage
                            src={l.image_urls?.[0]}
                            alt={l.card?.name ?? l.title}
                            cardName={l.card?.name}
                            game={l.card?.game}
                            className="w-full h-full"
                            compact
                          />
                        </Link>
                        <div className="flex-1 min-w-0">
                          <Link
                            to={`/listings/${listingId}`}
                            className="font-medium text-sm hover:text-brand-600 dark:hover:text-brand-400 truncate block transition-colors"
                          >
                            {l.card?.name ?? l.title}
                          </Link>
                          <p className="text-xs text-gray-500 dark:text-zinc-500">
                            {l.condition} · {l.seller?.username}
                          </p>
                          {l.trade_for && (
                            <p className="text-xs text-purple-600 dark:text-purple-400 mt-0.5 truncate">
                              Looking for: {l.trade_for}
                            </p>
                          )}
                        </div>
                        <span className="text-xs font-semibold text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 px-2 py-1 rounded-lg flex-shrink-0">
                          Trade
                        </span>
                        <Link
                          to={`/listings/${listingId}`}
                          className="px-3 py-2 border border-purple-300 dark:border-purple-700 text-purple-600 dark:text-purple-400 rounded-xl text-sm font-medium hover:bg-purple-50 dark:hover:bg-purple-900/20 flex-shrink-0 transition-colors"
                        >
                          View &amp; Offer
                        </Link>
                        <button
                          onClick={() => removeItem(listingId)}
                          className="text-gray-300 dark:text-zinc-700 hover:text-red-500 transition-colors flex-shrink-0"
                          title="Remove"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Footer */}
            <div className="flex justify-between items-center pt-1">
              <button
                onClick={fetchListings}
                className="flex items-center gap-1.5 text-sm text-gray-400 dark:text-zinc-600 hover:text-gray-600 dark:hover:text-zinc-400 transition-colors"
              >
                <RefreshCw className="h-3.5 w-3.5" /> Refresh
              </button>
              <button
                onClick={() => { if (window.confirm('Clear all items from cart?')) clearCart(); }}
                className="text-sm text-gray-400 dark:text-zinc-600 hover:text-red-500 transition-colors"
              >
                Clear cart
              </button>
            </div>

          </div>
        )}
      </div>
    </div>
  );
}
